import { LinkProps } from "react-router-dom"
import { HomeRegistrationFragment } from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { RESOURCE_TYPE_COLLECTION } from "../resource/constants"
import { getSchoolUrl } from "../utils/urls"

type getCertificateDownloadUrlProps = {
  pwContext: ReturnType<typeof usePathwrightContext>
  registration: HomeRegistrationFragment
}

//adapted from src/modules/home/learn/utils.js
export const getCertificateDownloadUrl = ({
  pwContext,
  registration
}: getCertificateDownloadUrlProps) => {
  // ensure resource has certificate and path is complete
  if (
    (registration.path?.progress ?? 0) < 1 ||
    !registration.path?.completion_date ||
    !pwContext.school.has_certificate ||
    !registration.resource?.has_certificate
  )
    return null
  // other users shouldn't have access to certificate
  if (!pwContext.me || pwContext.me.id !== registration.user.id) return null

  return getSchoolUrl(
    `/certgen/certificate/${registration.resource.slug}/${
      registration.path.id
    }?sso=${localStorage.getItem("auth_token")}`
  )
}

export const getRegistrationPathLink = (
  registration: HomeRegistrationFragment,
  options?: {
    // Whether the path link for learner paths should append the ?next_item=1 query param which will
    // result in linking the user directly to their next incomplete item in the path instead of the path view.
    enableNextItem: boolean
  }
) => {
  let link: LinkProps["to"] = {}

  if (registration) {
    const resourceSlug = registration.resource?.slug!
    const cohortId = registration.group?.id!
    const stepId = registration.path?.next_step_id

    // Override the enableNextItem option if the resource is a Collection, since we don't currently track next step ids for collections.
    // Also override if the path is complete, since there will be no next step to navigate to.
    const enableNextItem =
      registration.resource?.resource_type !== RESOURCE_TYPE_COLLECTION &&
      registration.path?.progress !== 1 &&
      options?.enableNextItem

    link = getPathLink({
      resourceSlug,
      cohortId,
      stepId,
      registrationRole: registration.role,
      enableNextItem
    })
  }

  return link
}

export const getPathLink = ({
  resourceSlug,
  cohortId,
  stepId,
  registrationRole,
  enableNextItem
}: {
  resourceSlug: string
  cohortId: number
  stepId?: number | null
  registrationRole?: number
  // Whether the path link for learner paths should append the ?next_item=1 query param which will
  // result in linking the user directly to their next incomplete item in the path instead of the path view.
  enableNextItem?: boolean
}) => {
  let pathname = `/library/${resourceSlug}/${cohortId}/path/`
  let searchParams = new URLSearchParams()

  if (registrationRole) {
    // Moderators- have Learn access to the Path, though Moderators also have Teach access.
    // If we're linking directly to the user's next item on their Path, we'll always use Learn mode.
    // Otherwise, we'll use Teach mode for Moderators.
    if (registrationRole <= 10) {
      // By default, link to the Learn path mode, though we may override this for Moderators.
      searchParams.set("path_mode", "5")

      if (enableNextItem) {
        // In some cases we may not have a stepId (like if the user's path hasn't been synced yet).
        if (stepId) {
          pathname += `step/${stepId}/`
        } else {
          // If we don't have a stepId, we'll append the ?next_item=1 query param which will
          // effectively link directly to the user's next incomplete item in the path.
          // This behavior is handled in the useNavigateToNextPathItem hook.
          searchParams.set("next_item", "1")
        }
      } else if (registrationRole === 10) {
        // Link to the Teach path mode for Moderators when enableNextItem is false.
        searchParams.set("path_mode", "10")
      }
    } else if (registrationRole === 15) {
      searchParams.set("path_mode", "10")
    } else if (registrationRole === 20) {
      searchParams.set("path_mode", "15")
    }
  }

  return {
    pathname,
    search: searchParams.toString(),
    state: {
      setResourceLaunchedFrom: true
    }
  }
}

//! This function is not being used in the codebase
//Maps a number between a threshold range to a value between 0 and 1
export const mapToRange = (
  value: number,
  minThreshold: number,
  maxThreshold: number
) => {
  // Ensure value is within the bounds of minThreshold and maxThreshold
  if (value < minThreshold || value > maxThreshold) {
    // throw new Error("Value out of bounds")
    console.debug("Value out of bounds:", value)
  }
  // if

  if (value >= maxThreshold) {
    return 1
  }
  // Normalize the value to a range between 0 and 1
  return (value - minThreshold) / (maxThreshold - minThreshold)
}

// Filter out any Source Cohort registrations for which there is only 1 active Cohort
// which the user is registered for (better UX).
export function filterOutSourceRegistration(
  registrations: HomeRegistrationFragment[]
) {
  return registrations.filter(
    (registration) =>
      // Include non-Source Cohorts.
      !registration.group?.is_source_cohort ||
      // Include Source Cohorts which don't have exactly 1 active Cohort.
      registration.resource?.groups?.total !== 1 ||
      // Include Source Cohorts for which the user doesn't have a Cohort registration.
      registrations.every(
        (r) => r.group?.source_cohort_id !== registration.group?.id
      )
  )
}

export function filterRootRegistration(registration: HomeRegistrationFragment) {
  return !registration.group?.parent_section_id
}

export function filterNestedRegistration(
  rootRegistration: HomeRegistrationFragment,
  registration: HomeRegistrationFragment
) {
  return rootRegistration.group?.id === registration.group?.parent_section_id
}
